import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Sparkles, Wallet, LineChart, GitCompareArrows, Compass, Target, Bot,
  Link2, Bell, Newspaper, ShieldCheck, Mail, ArrowRight, ArrowLeft,
  Crown, Check, PlayCircle, RotateCcw, TrendingUp, Smartphone,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import usePageSeo from "@/hooks/usePageSeo";
import ContactDialog from "@/components/ContactDialog";
import { useAuth } from "@/context/AuthContext";

/**
 * `/demo` — a self-contained, no-signup guided tour of a Pro user's typical
 * workflow through DPP. Each step is a mocked visual + a "why this matters"
 * callout so a first-time visitor sees exactly how the product earns its
 * $9.99/mo. Ends with a Contact-us CTA for anyone who still has questions.
 */
export default function Demo() {
  const [step, setStep] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [searchParams] = useSearchParams();
  // Auto-start playback when arriving from the landing "See DPP in Action"
  // card (`/demo?autoplay=1`). Falls back to manual control otherwise.
  const [autoplay, setAutoplay] = useState(searchParams.get("autoplay") === "1");
  const { user } = useAuth();
  const nav = useNavigate();

  const steps = useMemo(() => STEPS, []);
  const current = steps[step];
  const progress = Math.round(((step + 1) / steps.length) * 100);
  const isLast = step === steps.length - 1;

  React.useEffect(() => {
    if (!autoplay || isLast) return undefined;
    const t = setTimeout(() => setStep((s) => Math.min(s + 1, steps.length - 1)), 6000);
    return () => clearTimeout(t);
  }, [autoplay, step, isLast, steps.length]);

  usePageSeo({
    title: "Product Demo — Dividend Portfolio Planner",
    description: "60-second guided tour of a Pro user's workflow on DPP — plan a portfolio, backtest, and read AI explanations in real time.",
    path: "/demo",
    ogSlug: "demo",
  });

  const steps2 = null; // preserve original var ordering

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      {/* Header */}
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary flex items-center gap-2">
            <PlayCircle className="w-3.5 h-3.5" /> Product Demo · Pro workflow
          </div>
          <h1 className="serif text-4xl mt-2">How Pro users get the most out of DPP</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            Follow a Pro user's typical journey — from a blank portfolio to a live-tracked, AI-optimized, income-target-tuned setup. No sign-up needed to watch. Every step tells you both <em>how</em> and <em>why it matters</em>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setAutoplay((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 mono text-[10px] uppercase tracking-widest transition-colors border ${autoplay ? "bg-primary text-primary-foreground border-primary" : "border-primary/30 text-primary hover:bg-primary/10"}`}
            data-testid="demo-autoplay"
            aria-pressed={autoplay}
            title="Auto-advance every 6 seconds"
          >
            <PlayCircle className="w-3.5 h-3.5" /> Autoplay {autoplay ? "on" : "off"}
          </button>
          {step > 0 && (
            <Button variant="ghost" onClick={() => setStep(0)} className="rounded-full" data-testid="demo-restart">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restart
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar + step chips */}
      <div className="mb-6" data-testid="demo-progress">
        <div className="h-1 w-full bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-2 flex items-center justify-between mono text-[10px] uppercase tracking-widest text-muted-foreground">
          <span>Step {step + 1} of {steps.length}</span>
          <span>{progress}% complete</span>
        </div>
        <div className="mt-3 flex gap-1.5 flex-wrap">
          {steps.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setStep(i)}
              className={`px-2 h-6 rounded-full mono text-[9px] uppercase tracking-widest border transition-colors ${
                i === step
                  ? "bg-primary text-primary-foreground border-primary"
                  : i < step
                    ? "border-primary/40 text-primary hover:bg-primary/10"
                    : "border-border text-muted-foreground hover:border-primary/40"
              }`}
              data-testid={`demo-jump-${i}`}
            >
              {i + 1}. {s.chip}
            </button>
          ))}
        </div>
      </div>

      {/* Step card */}
      <Card className="overflow-hidden" data-testid={`demo-step-${step}`}>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_360px]">
          {/* Left: mock visual */}
          <div className="bg-muted/20 border-r border-border p-5 min-h-[420px] grid place-items-center">
            <div className="w-full">{current.visual}</div>
          </div>
          {/* Right: copy */}
          <div className="p-6 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-md bg-primary/10 grid place-items-center text-primary">
                <current.icon className="w-4 h-4" />
              </div>
              <Badge variant="outline" className="mono text-[9px] uppercase tracking-widest">
                {current.tier}
              </Badge>
              {current.newBadge && (
                <Badge variant="outline" className="mono text-[9px] uppercase tracking-widest text-secondary border-secondary/40">
                  {current.newBadge}
                </Badge>
              )}
            </div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Step {step + 1}
            </div>
            <h2 className="serif text-2xl mt-1 mb-2">{current.title}</h2>
            {current.navPath && (
              <div className="inline-flex items-center gap-1.5 rounded-full border border-secondary/40 bg-secondary/[0.06] px-3 py-1 mono text-[10px] uppercase tracking-widest text-secondary mb-3 self-start" data-testid="demo-navhint">
                <Compass className="w-3 h-3" /> Get there: {current.navPath}
              </div>
            )}
            <p className="text-sm text-foreground/85 leading-relaxed">{current.body}</p>
            <div className="mt-4 rounded-md border border-primary/20 bg-primary/[0.04] p-3">
              <div className="mono text-[9px] uppercase tracking-widest text-primary mb-1">Why this matters</div>
              <p className="text-sm text-foreground/85 leading-relaxed">{current.value}</p>
            </div>
            <div className="mt-auto pt-6 flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={() => setStep(Math.max(0, step - 1))}
                disabled={step === 0}
                className="rounded-full"
                data-testid="demo-prev"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
              </Button>
              {isLast ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" onClick={() => setContactOpen(true)} className="rounded-full" data-testid="demo-contact">
                    <Mail className="w-3.5 h-3.5 mr-1.5" /> Still have questions?
                  </Button>
                  <Button onClick={() => nav(user ? "/dashboard" : "/auth")} className="rounded-full" data-testid="demo-cta">
                    {user ? "Open my dashboard" : "Try DPP for free"} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </div>
              ) : (
                <Button onClick={() => setStep(step + 1)} className="rounded-full" data-testid="demo-next">
                  Next: {steps[step + 1].chip} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Footer strip — quick jump to critical actions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <Crown className="w-5 h-5 text-primary shrink-0" />
          <div>
            <div className="font-medium text-sm">Ready to try Pro?</div>
            <Link to="/pricing" className="mono text-[10px] uppercase tracking-widest text-primary hover:underline">See pricing →</Link>
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <Mail className="w-5 h-5 text-primary shrink-0" />
          <div>
            <div className="font-medium text-sm">Have questions?</div>
            <button onClick={() => setContactOpen(true)} className="mono text-[10px] uppercase tracking-widest text-primary hover:underline" data-testid="demo-footer-contact">Contact us →</button>
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-primary shrink-0" />
          <div>
            <div className="font-medium text-sm">Policies &amp; data</div>
            <Link to="/trust" className="mono text-[10px] uppercase tracking-widest text-primary hover:underline">Trust Center →</Link>
          </div>
        </CardContent></Card>
      </div>

      <ContactDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        defaultName={user?.name || ""}
        defaultEmail={user?.email || ""}
      />
    </div>
  );
}

// ---------- Mock visuals — full "app screen" mockups with side nav + top bar ----------

// Primary side-rail nav that mirrors the real DPP shell so every mock feels like a page
// you could actually click through. `active` matches the label of the current section.
const NAV = [
  { label: "Dashboard", icon: LineChart },
  { label: "Portfolio", icon: Wallet },
  { label: "Compare",   icon: GitCompareArrows },
  { label: "Holdings",  icon: Wallet },
  { label: "Research",  icon: Compass },
  { label: "Alerts",    icon: Bell },
  { label: "Newsletter",icon: Newspaper },
  { label: "Trust",     icon: ShieldCheck },
];

const AppShell = ({ path, active, title, subtitle, actions, tabs, activeTab, children, plan = "Pro" }) => (
  <div className="rounded-lg border border-border bg-card overflow-hidden shadow-md">
    {/* Traffic-light + URL bar */}
    <div className="flex items-center gap-1.5 px-3 py-2 bg-muted/60 border-b border-border">
      <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
      <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
      <span className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
      <div className="flex-1 mx-3 rounded-md bg-background border border-border h-5 flex items-center px-2 mono text-[9px] text-muted-foreground truncate">
        dividendportfolioplanner.com{path}
      </div>
      <span className="mono text-[8px] uppercase tracking-widest text-muted-foreground">DPP</span>
    </div>

    <div className="grid grid-cols-[52px_1fr] md:grid-cols-[112px_1fr] min-h-[360px]">
      {/* Side rail */}
      <aside className="border-r border-border bg-muted/30 py-2.5 px-1 md:px-1.5 space-y-0.5">
        <div className="px-1 md:px-2 pb-2 border-b border-border/60 mb-2">
          <div className="mono text-[9px] font-bold tracking-widest text-primary">DPP</div>
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground hidden md:block">{plan}</div>
        </div>
        {NAV.map((n) => {
          const isActive = n.label === active;
          const Icon = n.icon;
          return (
            <div
              key={n.label}
              className={`flex items-center gap-1.5 px-1 md:px-1.5 py-1 rounded text-[10px] transition-colors ${
                isActive
                  ? "bg-primary/10 text-primary font-semibold border-l-2 border-primary"
                  : "text-muted-foreground border-l-2 border-transparent"
              }`}
              title={n.label}
            >
              <Icon className="w-3 h-3 shrink-0" />
              <span className="truncate hidden md:inline">{n.label}</span>
            </div>
          );
        })}
      </aside>

      {/* Content column */}
      <div className="flex flex-col min-w-0">
        {/* Page header */}
        <div className="flex items-start justify-between gap-2 px-3 md:px-4 pt-3 pb-2 border-b border-border/60">
          <div className="min-w-0 flex-1">
            <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground truncate">{path}</div>
            <div className="serif text-[13px] md:text-base leading-tight mt-0.5 truncate">{title}</div>
            {subtitle && <div className="mono text-[9px] text-muted-foreground mt-0.5 truncate">{subtitle}</div>}
          </div>
          {actions && <div className="hidden md:flex items-center gap-1.5 shrink-0">{actions}</div>}
        </div>

        {/* Tab strip */}
        {tabs && (
          <div className="flex items-center gap-3 px-3 md:px-4 py-1.5 border-b border-border/60 bg-card/60 overflow-x-auto">
            {tabs.map((t) => (
              <span
                key={t}
                className={`mono text-[9px] uppercase tracking-widest whitespace-nowrap pb-1 ${
                  t === activeTab ? "text-primary border-b-2 border-primary" : "text-muted-foreground"
                }`}
              >
                {t}
              </span>
            ))}
          </div>
        )}

        <div className="p-3 md:p-4 flex-1 overflow-hidden">{children}</div>
      </div>
    </div>
  </div>
);

// Small helpers for consistent action-bar pills in the top-right of the page header
const PillBtn = ({ children, tone = "muted" }) => (
  <span
    className={`mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-full border ${
      tone === "primary" ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"
    }`}
  >
    {children}
  </span>
);

const MockPortfolio = () => (
  <AppShell
    plan="Free"
    path="/portfolio/new"
    active="Portfolio"
    title="Dividend Growth · Draft"
    subtitle="4 tickers · Weights auto-normalized"
    tabs={["Builder", "Backtest", "Compare", "Save"]}
    activeTab="Builder"
    actions={<><PillBtn>Save draft</PillBtn><PillBtn tone="primary">Run backtest</PillBtn></>}
  >
    <div className="grid grid-cols-[1fr_130px] gap-3">
      <div>
        <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">Allocation</div>
        {[
          ["SCHD", 40, "bg-primary",     "US Div Equity"],
          ["VYM",  25, "bg-secondary",   "High Div Yield"],
          ["JEPI", 20, "bg-primary/60",  "Prem Income"],
          ["O",    15, "bg-secondary/60","Realty Income"],
        ].map(([t, w, bg, name]) => (
          <div key={t} className="flex items-center gap-2 mb-1.5">
            <div className="w-16">
              <div className="mono text-xs font-semibold leading-tight">{t}</div>
              <div className="mono text-[8px] text-muted-foreground truncate">{name}</div>
            </div>
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden"><div className={`${bg} h-full transition-all`} style={{ width: `${w}%` }} /></div>
            <span className="mono text-xs w-9 text-right">{w}%</span>
          </div>
        ))}
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border/60">
          <span className="mono text-[9px] uppercase tracking-widest text-muted-foreground">+ Add ticker</span>
          <input readOnly placeholder="e.g. KO, PG, MO" className="flex-1 h-6 rounded-md border border-border bg-background px-2 mono text-[10px]" />
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="rounded-md border border-border p-1.5">
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Portfolio yield</div>
          <div className="serif text-base text-primary leading-tight">4.4%</div>
        </div>
        <div className="rounded-md border border-border p-1.5">
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Safety</div>
          <div className="serif text-base leading-tight">78 / 100</div>
        </div>
        <div className="rounded-md border border-primary/30 bg-primary/[0.05] p-1.5">
          <label className="mono text-[8px] uppercase tracking-widest text-primary inline-flex items-center gap-1">
            <span className="w-6 h-3.5 rounded-full bg-primary relative shrink-0"><span className="absolute right-0.5 top-0.5 w-2.5 h-2.5 rounded-full bg-white" /></span>
            DRIP on
          </label>
          <div className="mono text-[8px] text-muted-foreground mt-0.5">Reinvest dividends</div>
        </div>
      </div>
    </div>
  </AppShell>
);

const MockBacktest = () => (
  <AppShell
    plan="Free"
    path="/portfolio/backtest"
    active="Portfolio"
    title="20-year backtest · $10k initial"
    subtitle="Jan 2004 → Feb 2026 · DRIP reinvested at real ex-div close"
    tabs={["Builder", "Backtest", "Compare", "Save"]}
    activeTab="Backtest"
    actions={<><PillBtn>Export CSV</PillBtn><PillBtn tone="primary">AI narrative</PillBtn></>}
  >
    <div className="grid grid-cols-[1fr_120px] gap-3">
      <div>
        <svg viewBox="0 0 200 80" className="w-full h-24">
          <defs><linearGradient id="g" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="hsl(var(--primary))" stopOpacity="0.35" /><stop offset="1" stopColor="hsl(var(--primary))" stopOpacity="0" /></linearGradient></defs>
          {[10,20,30,40,50,60,70].map((y) => <line key={y} x1="0" x2="200" y1={y} y2={y} stroke="hsl(var(--border))" strokeWidth="0.3" />)}
          <polyline points="0,72 20,66 40,60 60,52 80,46 100,34 120,28 140,22 160,14 180,10 200,4" fill="none" stroke="hsl(var(--primary))" strokeWidth="2" />
          <polygon points="0,72 20,66 40,60 60,52 80,46 100,34 120,28 140,22 160,14 180,10 200,4 200,80 0,80" fill="url(#g)" />
          <polyline points="0,72 20,68 40,64 60,60 80,56 100,52 120,48 140,44 160,40 180,36 200,32" fill="none" stroke="hsl(var(--secondary))" strokeWidth="1.5" strokeDasharray="3 2" />
          <circle cx="200" cy="4" r="1.8" fill="hsl(var(--primary))" />
          <text x="196" y="10" fontSize="6" textAnchor="end" fill="hsl(var(--primary))">$48.7k</text>
        </svg>
        <div className="flex items-center justify-between mono text-[8px] text-muted-foreground mt-0.5 px-0.5">
          <span>2004</span><span>2010</span><span>2016</span><span>2022</span><span>Today</span>
        </div>
        <div className="flex items-center gap-3 mt-2 mono text-[9px]">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-primary" />DRIP on</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-0.5 bg-secondary" />Cash divs</span>
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="rounded-md border border-border p-1.5">
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Ending</div>
          <div className="serif text-sm leading-tight">$48,720</div>
        </div>
        <div className="rounded-md border border-border p-1.5">
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Total return</div>
          <div className="serif text-sm text-primary leading-tight">+387%</div>
        </div>
        <div className="rounded-md border border-border p-1.5">
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">YoC today</div>
          <div className="serif text-sm leading-tight">12.4%</div>
        </div>
        <div className="rounded-md border border-border p-1.5">
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Max DD</div>
          <div className="serif text-sm leading-tight">-24%</div>
        </div>
      </div>
    </div>
  </AppShell>
);

const MockCompare = () => (
  <AppShell
    plan="Free"
    path="/compare"
    active="Compare"
    title="Side-by-side scenarios"
    subtitle="Same start · Same DRIP · Real prices"
    tabs={["Portfolios", "Metrics", "Chart", "Notes"]}
    activeTab="Metrics"
    actions={<><PillBtn>+ Add scenario</PillBtn><PillBtn tone="primary">Chart all 3</PillBtn></>}
  >
    <div className="grid grid-cols-5 mono text-[9px] uppercase tracking-widest text-muted-foreground pb-1 border-b border-border/60">
      <span className="col-span-2">Portfolio</span><span>End $</span><span>YoC</span><span>Max DD</span>
    </div>
    {[
      ["Aggressive growth", "SCHD 60 · QQQ 40", "$56k", "3.1%", "-38%", "text-primary bg-primary/[0.05]"],
      ["Balanced income",   "SCHD 40 · VYM 30 · JEPI 30", "$48k", "4.4%", "-24%", ""],
      ["Steady dividend",   "SCHD 30 · O 30 · KO 40", "$42k", "5.8%", "-15%", "text-muted-foreground"],
    ].map(([n, mix, v, y, d, cls], i) => (
      <div key={n} className={`grid grid-cols-5 items-center text-sm border-b border-border/40 py-1.5 last:border-b-0 ${cls}`}>
        <div className="col-span-2">
          <div className="mono text-[11px] font-semibold flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${i === 0 ? "bg-primary" : i === 1 ? "bg-secondary" : "bg-muted-foreground"}`} />
            {n}
          </div>
          <div className="mono text-[8px] text-muted-foreground truncate">{mix}</div>
        </div>
        <span className="serif text-sm">{v}</span>
        <span className="mono text-xs">{y}</span>
        <span className="mono text-xs">{d}</span>
      </div>
    ))}
    <div className="mt-2 rounded-md border border-primary/20 bg-primary/[0.04] p-2 flex items-center justify-between">
      <div className="mono text-[9px] text-primary">Winner (risk-adjusted): <span className="font-semibold">Balanced income</span></div>
      <span className="mono text-[8px] text-muted-foreground">Sharpe 0.94 · calm ride</span>
    </div>
  </AppShell>
);

const MockHoldings = () => (
  <AppShell
    path="/holdings"
    active="Holdings"
    title="My Holdings · Roth IRA"
    subtitle="Live · Synced 2m ago via SnapTrade"
    tabs={["All accounts", "Roth IRA", "Taxable", "401k", "+ Add"]}
    activeTab="Roth IRA"
    actions={<><PillBtn>Refresh</PillBtn><PillBtn tone="primary">+ Add position</PillBtn></>}
  >
    <div className="grid grid-cols-3 gap-2 mb-2">
      <div className="rounded-md border border-border p-1.5">
        <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Portfolio value</div>
        <div className="serif text-sm leading-tight">$9,286</div>
      </div>
      <div className="rounded-md border border-border p-1.5">
        <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Fwd income /yr</div>
        <div className="serif text-sm text-primary leading-tight">$432</div>
      </div>
      <div className="rounded-md border border-border p-1.5">
        <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Fwd /mo</div>
        <div className="serif text-sm leading-tight">$36</div>
      </div>
    </div>
    <div className="grid grid-cols-6 gap-1 mono text-[9px] uppercase tracking-widest text-muted-foreground pb-1 border-b border-border/60">
      <span>Ticker</span><span>Shares</span><span>Value</span><span>YoC</span><span>Fwd/yr</span><span>Ex-div</span>
    </div>
    {[
      ["SCHD", 60, "$5,124", "4.1%", "$210", "in 2d",  "text-primary"],
      ["JEPI", 30, "$1,752", "8.2%", "$144", "Fri",    ""],
      ["VYM",  20, "$2,410", "3.4%", "$78",  "Mar 15", ""],
    ].map((r, i) => (
      <div key={r[0]} className={`grid grid-cols-6 gap-1 text-sm border-b border-border/40 py-1 last:border-b-0 ${i === 0 ? "bg-primary/[0.03]" : ""}`}>
        <span className="mono font-semibold">{r[0]}</span>
        <span className="mono">{r[1]}</span>
        <span>{r[2]}</span>
        <span className={`mono ${r[6]}`}>{r[3]}</span>
        <span className="serif">{r[4]}</span>
        <span className={`mono text-[10px] ${i === 0 ? "text-primary font-semibold" : "text-muted-foreground"}`}>{r[5]}</span>
      </div>
    ))}
    <div className="mono text-[9px] text-primary pt-2 flex items-center justify-between border-t border-border/60 mt-2">
      <span className="inline-flex items-center gap-1"><Bell className="w-2.5 h-2.5" /> Push alert: SCHD ex-div in 2 days</span>
      <span className="text-muted-foreground">Cost basis: $8,214 · Gain +13%</span>
    </div>
  </AppShell>
);

const MockSnapTrade = () => (
  <AppShell
    path="/holdings/connect"
    active="Holdings"
    title="Connect a brokerage"
    subtitle="Read-only via SnapTrade · one-tap link"
    tabs={["All accounts", "Roth IRA", "Taxable", "+ Connect"]}
    activeTab="+ Connect"
    actions={<PillBtn>Skip · paste manually</PillBtn>}
  >
    <div className="text-center pt-1 pb-2">
      <div className="w-11 h-11 rounded-full bg-primary/10 grid place-items-center mx-auto text-primary mb-1.5">
        <Link2 className="w-5 h-5" />
      </div>
      <div className="serif text-base leading-tight">Link your brokerage in one tap</div>
      <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">Powered by SnapTrade · Read-only OAuth</div>
    </div>
    <div className="grid grid-cols-3 gap-1.5">
      {[
        ["Fidelity", "text-primary border-primary/40"],
        ["Schwab",   ""],
        ["Vanguard", ""],
        ["Robinhood", ""],
        ["E*TRADE",  ""],
        ["Merrill",  ""],
      ].map(([b, cls]) => (
        <div key={b} className={`rounded-md border py-1.5 text-[11px] text-center hover:border-primary/40 transition-colors ${cls || "border-border"}`}>{b}</div>
      ))}
    </div>
    <div className="mt-2 pt-2 border-t border-border/60 flex items-center justify-between mono text-[9px] text-muted-foreground">
      <span className="inline-flex items-center gap-1"><ShieldCheck className="w-2.5 h-2.5 text-primary" /> Fernet-encrypted at rest</span>
      <span>DPP never places trades</span>
    </div>
  </AppShell>
);

const MockOptimizer = () => (
  <AppShell
    path="/dashboard/optimizer"
    active="Dashboard"
    title="AI Portfolio Optimizer"
    subtitle="Goal: Max yield · Safety ≥ 70 · No single position > 30%"
    tabs={["Overview", "Optimizer", "Income Target", "Rebalance log"]}
    activeTab="Optimizer"
    actions={<><PillBtn>Reset</PillBtn><PillBtn tone="primary">Apply changes</PillBtn></>}
  >
    <div className="grid grid-cols-[1fr_120px] gap-3">
      <div>
        <div className="grid grid-cols-5 mono text-[9px] uppercase tracking-widest text-muted-foreground pb-1 border-b border-border/60">
          <span>Ticker</span><span>Current</span><span></span><span>Target</span><span>Δ</span>
        </div>
        {[
          ["SCHD", "40%", "30%", "-10%", ""],
          ["JEPI", "20%", "30%", "+10%", "text-primary"],
          ["VYM",  "25%", "20%",  "-5%", ""],
          ["O",    "15%", "20%",  "+5%", "text-primary"],
        ].map(([t, from, to, delta, cls]) => (
          <div key={t} className="grid grid-cols-5 items-center py-1 border-b border-border/40 last:border-b-0 text-xs">
            <span className="mono font-semibold">{t}</span>
            <span className="mono text-muted-foreground">{from}</span>
            <ArrowRight className="w-3 h-3 text-muted-foreground" />
            <span className="mono font-semibold">{to}</span>
            <span className={`mono ${cls}`}>{delta}</span>
          </div>
        ))}
      </div>
      <div className="space-y-1.5">
        <div className="rounded-md border border-border p-1.5">
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Yield</div>
          <div className="serif text-[11px] text-primary leading-tight">4.2 → 5.8%</div>
        </div>
        <div className="rounded-md border border-border p-1.5">
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Safety</div>
          <div className="serif text-[11px] leading-tight">72 → 78</div>
        </div>
        <div className="rounded-md border border-border p-1.5">
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Fwd income</div>
          <div className="serif text-[11px] text-primary leading-tight">+$92/mo</div>
        </div>
        <div className="rounded-md border border-primary/30 bg-primary/[0.05] p-1.5">
          <div className="mono text-[8px] uppercase tracking-widest text-primary flex items-center gap-1"><Bot className="w-2.5 h-2.5" />AI verdict</div>
          <div className="mono text-[9px] leading-tight mt-0.5">Boost income, keep safety</div>
        </div>
      </div>
    </div>
  </AppShell>
);

const MockIncome = () => (
  <AppShell
    path="/dashboard/income-target"
    active="Dashboard"
    title="Income Target Planner"
    subtitle="Reverse-solve capital needed for a monthly paycheck"
    tabs={["Overview", "Optimizer", "Income Target", "Rebalance log"]}
    activeTab="Income Target"
    actions={<><PillBtn>15-yr horizon</PillBtn><PillBtn tone="primary">Save plan</PillBtn></>}
  >
    <div className="rounded-md border border-primary/30 bg-primary/[0.04] p-2 flex items-center gap-2 mb-2">
      <Target className="w-4 h-4 text-primary shrink-0" />
      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Solve for</div>
      <input readOnly value="$2,000 / month" className="flex-1 bg-transparent serif text-base text-primary outline-none" />
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div className="rounded-md border border-border p-2">
        <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">Lump sum today</div>
        <div className="serif text-xl text-primary leading-tight mt-0.5">$382,000</div>
        <div className="mono text-[9px] text-muted-foreground mt-1">Invest today, DRIP on · hits target Feb 2026</div>
      </div>
      <div className="rounded-md border border-border p-2">
        <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">Or DCA / mo</div>
        <div className="serif text-xl leading-tight mt-0.5">$1,240</div>
        <div className="mono text-[9px] text-muted-foreground mt-1">15-yr horizon · hits target Feb 2041</div>
      </div>
    </div>
    <div className="mt-2 pt-2 border-t border-border/60 grid grid-cols-3 gap-2 mono text-[9px]">
      <div><div className="text-muted-foreground uppercase tracking-widest">Blended YoC</div><div className="serif text-[11px] mt-0.5">5.2%</div></div>
      <div><div className="text-muted-foreground uppercase tracking-widest">5-yr div growth</div><div className="serif text-[11px] mt-0.5">+9.4%/yr</div></div>
      <div><div className="text-muted-foreground uppercase tracking-widest">Tickers</div><div className="serif text-[11px] mt-0.5">SCHD·VYM·O</div></div>
    </div>
  </AppShell>
);

const MockResearch = () => (
  <AppShell
    path="/research/SCHD"
    active="Research"
    title="SCHD · Schwab US Div Equity ETF"
    subtitle="Safety 84 · Yield 3.7% · 11 yr payer"
    tabs={["Snapshot", "Dividends", "AI thesis", "Ask a question"]}
    activeTab="AI thesis"
    actions={<><PillBtn>Add to portfolio</PillBtn><PillBtn tone="primary">Save</PillBtn></>}
  >
    <div className="grid grid-cols-3 gap-1.5 mb-2">
      <div className="rounded-md border border-border p-1.5">
        <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Yield</div>
        <div className="serif text-sm text-primary leading-tight">3.7%</div>
      </div>
      <div className="rounded-md border border-border p-1.5">
        <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">5-yr div growth</div>
        <div className="serif text-sm leading-tight">+11%/yr</div>
      </div>
      <div className="rounded-md border border-border p-1.5">
        <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Payout ratio</div>
        <div className="serif text-sm leading-tight">64%</div>
      </div>
    </div>
    <div className="text-[11px] italic text-foreground/80 leading-relaxed rounded-md bg-muted/40 p-2 border border-border/60">
      <div className="mono text-[8px] uppercase tracking-widest text-primary not-italic mb-1 flex items-center gap-1"><Bot className="w-2.5 h-2.5" /> AI thesis · grounded in real data</div>
      "SCHD screens for 10-year dividend payers with strong cash flow. Reliable 3.7% yield with 11% 5-yr dividend growth — the workhorse of most income sleeves. Payout ratio of 64% leaves runway for hikes even in a mild recession…"
    </div>
    <div className="mt-2 rounded-full border border-primary/30 bg-primary/[0.04] px-3 py-1.5 flex items-center gap-2">
      <Bot className="w-3 h-3 text-primary shrink-0" />
      <input placeholder="Ask a follow-up (e.g. 'is the payout ratio sustainable?')" className="bg-transparent flex-1 outline-none mono text-[10px] text-muted-foreground" readOnly />
      <ArrowRight className="w-3 h-3 text-primary" />
    </div>
  </AppShell>
);

const MockPush = () => (
  <AppShell
    path="/holdings"
    active="Alerts"
    title="Push notifications"
    subtitle="Ex-dividend heads-up · one push per ticker"
    tabs={["Ex-dividend", "Price", "Dividend cuts", "Settings"]}
    activeTab="Ex-dividend"
    actions={<><PillBtn>Install app</PillBtn><PillBtn tone="primary">Enable pushes</PillBtn></>}
  >
    <div className="grid grid-cols-[1fr_140px] gap-3">
      <div className="space-y-1.5">
        <div className="rounded-md border border-primary/30 bg-primary/[0.06] px-3 py-2">
          <div className="mono text-[9px] font-semibold text-primary flex items-center gap-1">
            <Bell className="w-2.5 h-2.5" /> DPP · yesterday 8:00am
          </div>
          <div className="text-sm mt-1 leading-tight">SCHD goes ex-dividend tomorrow — projected $34.20 on your 60 shares.</div>
          <div className="mono text-[8px] text-muted-foreground mt-1">Tap to open Holdings → SCHD</div>
        </div>
        <div className="rounded-md border border-border bg-card px-3 py-2 opacity-80">
          <div className="mono text-[9px] font-semibold text-secondary flex items-center gap-1">
            <Bell className="w-2.5 h-2.5" /> DPP · Fri 8:00am
          </div>
          <div className="text-sm mt-1 leading-tight">JEPI ex-div Fri — projected $18.60</div>
        </div>
        <div className="rounded-md border border-border bg-card px-3 py-2 opacity-60">
          <div className="mono text-[9px] font-semibold text-muted-foreground flex items-center gap-1">
            <Bell className="w-2.5 h-2.5" /> DPP · Mar 15
          </div>
          <div className="text-sm mt-1 leading-tight">VYM ex-div Mar 15 — projected $9.10</div>
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="rounded-md border border-border p-1.5 text-center">
          <Smartphone className="w-6 h-6 mx-auto text-primary" />
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground mt-1">Install as PWA</div>
          <div className="mono text-[9px] mt-0.5">iOS · Android · Desktop</div>
        </div>
        <div className="rounded-md border border-border p-1.5">
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Enabled</div>
          <div className="serif text-sm text-primary leading-tight">7 tickers</div>
        </div>
      </div>
    </div>
  </AppShell>
);

const MockNewsletter = () => (
  <AppShell
    path="/dashboard/newsletter"
    active="Newsletter"
    title="The DPP Weekly · Preview"
    subtitle="Personalized to your holdings · sent every Saturday 8:00am ET"
    tabs={["Preview", "Schedule", "Subscribers", "Unsubscribes"]}
    activeTab="Preview"
    actions={<><PillBtn>Send test</PillBtn><PillBtn tone="primary">Schedule</PillBtn></>}
  >
    <div className="rounded-md border border-border bg-background p-3 space-y-1.5">
      <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground flex items-center justify-between">
        <span>From: weekly@dividendportfolioplanner.com</span>
        <span>Sat 8:00am ET</span>
      </div>
      <div className="serif text-base text-primary leading-tight">Your Saturday dividend digest</div>
      <div className="mono text-[9px] text-muted-foreground italic">Hi Alex — here's what's happening in YOUR portfolio this week.</div>
      <div className="pt-1.5 border-t border-border/60">
        <div className="mono text-[9px] text-secondary font-semibold">📊 Your holdings</div>
        <div className="mono text-[9px] text-foreground/80 mt-0.5">SCHD (60) · JEPI (30) · VYM (20) · O (15)</div>
      </div>
      <div>
        <div className="mono text-[9px] text-secondary font-semibold">📅 Ex-dividend this week</div>
        <ul className="mono text-[9px] text-foreground/80 pl-2 leading-relaxed mt-0.5 space-y-0.5">
          <li>· SCHD: ex-div in 2 days · projected <span className="text-primary font-semibold">$34.20</span></li>
          <li>· O: ex-div in 4 days · projected <span className="text-primary font-semibold">$3.60</span></li>
        </ul>
      </div>
      <div>
        <div className="mono text-[9px] text-secondary font-semibold">💡 This week's Investment 101</div>
        <div className="mono text-[9px] text-foreground/80 mt-0.5">What "yield-on-cost" really means (in 90 seconds)…</div>
      </div>
      <div className="mono text-[8px] text-muted-foreground italic pt-1 border-t border-border/60">One-click unsubscribe · we never share your email</div>
    </div>
  </AppShell>
);

const MockContact = () => (
  <AppShell
    path="/trust"
    active="Trust"
    title="Trust Center · Contact & Support"
    subtitle="Policies, transparency, and a real human on the other end"
    tabs={["Policies", "Data & security", "FAQ", "Contact us"]}
    activeTab="Contact us"
    actions={<><PillBtn>Download PDFs</PillBtn><PillBtn tone="primary">Open chatbot</PillBtn></>}
  >
    <div className="grid grid-cols-[1fr_150px] gap-3 items-start">
      <div className="space-y-2">
        <div className="text-[12px] leading-relaxed">
          Every policy is public and downloadable. When a version bumps, we ask you to re-accept in a blocking modal — no dark patterns. The AI chatbot escalates cleanly to Contact us with the full transcript so nobody makes you repeat yourself.
        </div>
        <div className="rounded-md border border-primary/30 bg-primary/[0.04] p-2">
          <div className="mono text-[9px] uppercase tracking-widest text-primary mb-1">Reach us</div>
          <div className="grid grid-cols-3 gap-1.5">
            <div className="rounded-md border border-border p-1.5 text-center"><Bot className="w-3.5 h-3.5 mx-auto text-primary" /><div className="mono text-[8px] mt-0.5">Chatbot</div></div>
            <div className="rounded-md border border-border p-1.5 text-center"><Mail className="w-3.5 h-3.5 mx-auto text-primary" /><div className="mono text-[8px] mt-0.5">Email</div></div>
            <div className="rounded-md border border-border p-1.5 text-center"><PlayCircle className="w-3.5 h-3.5 mx-auto text-primary" /><div className="mono text-[8px] mt-0.5">YouTube</div></div>
          </div>
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="rounded-md border border-border p-1.5">
          <ShieldCheck className="w-4 h-4 text-primary" />
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground mt-1">Terms v3.2</div>
          <div className="mono text-[9px] text-primary">Accepted ✓</div>
        </div>
        <div className="rounded-md border border-border p-1.5">
          <ShieldCheck className="w-4 h-4 text-primary" />
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground mt-1">Privacy v2.1</div>
          <div className="mono text-[9px] text-primary">Accepted ✓</div>
        </div>
        <div className="rounded-md border border-primary/30 bg-primary/[0.05] p-1.5">
          <div className="mono text-[8px] uppercase tracking-widest text-primary">Certificate</div>
          <div className="mono text-[9px]">Download PDF</div>
        </div>
      </div>
    </div>
  </AppShell>
);

// ---------- Step catalog ----------
const STEPS = [
  {
    chip: "Portfolio",
    tier: "Free · Step 1",
    icon: Wallet,
    navPath: "Sign in → Dashboard → New portfolio",
    title: "Design a dividend portfolio in under a minute",
    body: "Pick tickers, drag the weight sliders (or paste a CSV like `SCHD,40 · VYM,25 · JEPI,20 · O,15`). Toggle DRIP on if you want dividends to reinvest. Weights auto-normalize to 100% so you never fight the math.",
    value: "Skip the spreadsheet. Get from idea to testable portfolio in the time it takes to make coffee — the mechanical work of building a scenario is now a slider drag.",
    visual: <MockPortfolio />,
  },
  {
    chip: "Backtest",
    tier: "Free · Step 2",
    icon: LineChart,
    navPath: "Portfolio → Run backtest tab",
    title: "Backtest against 30+ years of real history",
    body: "One click runs the portfolio against real daily prices and dividends from Yahoo Finance. See ending value, total return, yield-on-cost, and per-ticker contribution. DRIP reinvests on the real ex-dividend date at that day's closing price — no shortcuts.",
    value: "Instead of guessing whether a strategy would have worked, you see the actual trajectory a real investor with the same allocation would have lived through — including 2008, 2020, and every dividend cut in between.",
    visual: <MockBacktest />,
  },
  {
    chip: "Compare",
    tier: "Free · Step 3",
    icon: GitCompareArrows,
    navPath: "Nav → Compare",
    title: "Compare scenarios side by side",
    body: "Save the aggressive growth version, the balanced version, and the steady-income version. Stand them next to each other on the same start date, same DRIP settings, and see which one delivered.",
    value: "The best portfolio for you is usually a trade-off — the compare view exposes those trade-offs visually so you pick with your eyes open, not your gut.",
    visual: <MockCompare />,
  },
  {
    chip: "Track live",
    tier: "Free · Step 4",
    icon: Wallet,
    navPath: "Nav → My Holdings → + Add account",
    title: "Track your real holdings across every account",
    body: "Paste your real positions (or link them — see next step). Create separately named accounts: Roth · Taxable · 401k · IRA · HSA. See live portfolio value, unrealized gain, yield-on-cost, and forward monthly + annual dividend income.",
    value: "Your brokerage shows you 'balance today'. DPP shows you 'income tomorrow' — the exact projected dividend cash for every position, so you know what's coming before it hits your account.",
    visual: <MockHoldings />,
  },
  {
    chip: "Broker sync",
    tier: "Free · Step 5",
    newBadge: "New",
    icon: Link2,
    navPath: "My Holdings → + Connect brokerage",
    title: "Sync your brokerage read-only, no copy-paste",
    body: "Tap Connect brokerage, pick your broker in the SnapTrade portal (Schwab, Fidelity, Vanguard, Robinhood, IBKR, and 40+ others), and DPP imports positions + cost basis + cash automatically. Read-only OAuth — DPP never sees your password and can never place trades. Your SnapTrade secret is Fernet-encrypted at rest.",
    value: "The single biggest reason people give up on tracking tools is the paste step. SnapTrade removes it. Once linked, your holdings stay fresh forever with a one-tap refresh.",
    visual: <MockSnapTrade />,
  },
  {
    chip: "AI Optimizer",
    tier: "Pro · Step 6",
    icon: Bot,
    navPath: "Dashboard → Optimizer tab (Pro)",
    title: "AI Portfolio Optimizer — rebalance for a goal",
    body: "Tell DPP what you want (max yield · max total return · balanced · minimize drawdown) and any constraints (min safety score, sector caps, max single position). The Optimizer proposes concrete weight changes to hit that goal, with the projected impact next to each move.",
    value: "This is why Pro exists. Instead of manually testing dozens of allocations, one click gives you the mathematically best allocation for your goal — turning what used to be a full weekend of spreadsheet work into a coffee break.",
    visual: <MockOptimizer />,
  },
  {
    chip: "Income Target",
    tier: "Pro · Step 7",
    icon: Target,
    navPath: "Dashboard → Income Target tab (Pro)",
    title: "Reverse-solve for a monthly income goal",
    body: "Type in the number you want to earn in dividends per month — say $2,000. DPP reverse-solves the exact lump sum + optional monthly DCA needed to get there against your chosen tickers' real yield-on-cost and 5-year dividend growth.",
    value: "Most tools show you what your portfolio would earn. Income Target Planner does the inverse — it tells you exactly how much runway you need to buy a specific paycheck. That's the number you actually need to plan around.",
    visual: <MockIncome />,
  },
  {
    chip: "Research",
    tier: "Pro · Step 8",
    icon: Compass,
    navPath: "Nav → Research → search a ticker (Pro)",
    title: "AI ticker research + follow-up chat",
    body: "Every ticker has an AI-written thesis grounded in real data: dividend safety score, payout ratio history, 62-year dividend history, ex-div calendar, and recent news. Ask follow-ups — 'is the payout ratio sustainable?' — and get answers pulled from the same real numbers.",
    value: "Pro Research replaces 8 open tabs (Yahoo, Simply Safe, Seeking Alpha, brokerage research…) with one place — and lets you ask questions in plain English instead of digging through 10-Ks.",
    visual: <MockResearch />,
  },
  {
    chip: "Push alerts",
    tier: "Free · Step 9",
    newBadge: "New",
    icon: Bell,
    navPath: "Footer → Get the app → Enable notifications on Holdings",
    title: "Never miss an ex-dividend date again",
    body: "Install DPP as a Progressive Web App (iOS, Android, or desktop — from /download). Once installed you get a phone push the day before every ex-dividend date across every ticker in every account. No App Store required.",
    value: "The ex-dividend date is the single most important date in dividend investing — miss it by one day and you miss the check. Push alerts turn missed dividends into a solved problem.",
    visual: <MockPush />,
  },
  {
    chip: "Newsletter",
    tier: "Free · Step 10",
    newBadge: "Portfolio-personalized",
    icon: Newspaper,
    navPath: "Avatar menu → Weekly newsletter → Subscribe",
    title: "The DPP Weekly — YOUR portfolio, every Saturday",
    body: "Every Saturday at 8am ET we email you a digest built from YOUR tracked holdings — not a generic market wrap. It lists the tickers you own, ex-dividend events landing in the next 7 days with projected payout amounts, and one plain-English Investment 101 concept.",
    value: "Most newsletters tell you what SOMEONE ELSE is buying. The DPP Weekly tells you what's happening in YOUR portfolio this week. That personalization turns a weekly checkbox into weekly signal — you always know what's incoming before you open the app.",
    visual: <MockNewsletter />,
  },
  {
    chip: "Trust & Contact",
    tier: "Every plan · Step 11",
    icon: ShieldCheck,
    navPath: "Footer → Trust Center · Chatbot bubble (bottom-right)",
    title: "Full transparency + a human on the other end",
    body: "Every policy is public at /trust. Download PDFs anytime. When a policy version bumps, we ask you to re-accept in a blocking modal — no dark patterns. And when you need a person, the chatbot escalates cleanly to Contact us with the full transcript so nobody makes you repeat yourself.",
    value: "You're trusting DPP with your holdings. That deserves policies you can actually read + a way to reach an accountable human. Both are one click away from every page.",
    visual: <MockContact />,
  },
];
